import React from 'react';
import type { FreelanceHubStats } from '../../../types/freelance-hub';
import { generateFreelanceMarketHeaderText } from '../../../utils/freelance/generate-freelance-market-header-text';
import { buildFaqPageSchema } from '../../../utils/seo/build-faq-page-schema';
import { buildLocationSrpPath } from '../../../utils/location/build-location-srp-path';
import { freelanceJobTypeFacetForLocale } from '../../../utils/job/freelance-job-type';

export interface FreelanceHeaderProps {
  locale: 'en' | 'fi' | 'sv';
  freelanceHubData: FreelanceHubStats | null;
  className?: string;
}

const JOBS_SEGMENT_BY_LOCALE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'jobs',
  fi: 'tyopaikat',
  sv: 'lediga-jobb',
};

const H1_BY_LOCALE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'Freelance Jobs in Finland',
  fi: 'Keikkatyöpaikat Suomessa',
  sv: 'Frilansjobb i Finland',
};

const FAQ_SECTION_TITLE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'Frequently asked questions',
  fi: 'Usein kysytyt kysymykset',
  sv: 'Vanliga frågor',
};

const POPULAR_LOCATIONS_TITLE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'Popular locations',
  fi: 'Suositut sijainnit',
  sv: 'Populära orter',
};

const POPULAR_FIELDS_TITLE: Record<'en' | 'fi' | 'sv', string> = {
  en: 'Popular fields',
  fi: 'Suositut alat',
  sv: 'Populära områden',
};

// Combined with the jobType=Freelance filter via query param so "Popular locations"/"Popular
// fields" link to the genuine sub-query (e.g. "Freelance jobs in Helsinki"), not a plain
// location/field page that drops the freelance filter — same reasoning as CategoryHeader's
// buildLocationUrl (TEC-322), applied to jobType since there's no pretty combined path for it.
function buildFreelanceLocationUrl(locale: 'en' | 'fi' | 'sv', location: string): string {
  const jobType = freelanceJobTypeFacetForLocale(locale);
  return `/${locale}${buildLocationSrpPath(location, locale)}?jobType=${encodeURIComponent(jobType)}`;
}

function buildFreelanceFieldUrl(locale: 'en' | 'fi' | 'sv', field: string): string {
  const jobType = freelanceJobTypeFacetForLocale(locale);
  return `/${locale}/${JOBS_SEGMENT_BY_LOCALE[locale]}?field=${encodeURIComponent(field)}&jobType=${encodeURIComponent(jobType)}`;
}

/**
 * Static SEO header for the `/jobs/freelance` hub page: static H1 + data-driven intro,
 * popular-locations and popular-fields internal links, and a visible FAQ accordion (with
 * matching FAQPage JSON-LD) — everything present in the initial server-rendered HTML.
 * Rendered by SearchHeader.tsx as a third branch, after location and category.
 */
export const FreelanceHeader: React.FC<FreelanceHeaderProps> = ({ locale, freelanceHubData, className = '' }) => {
  const jobCount = freelanceHubData?.jobCount ?? 0;
  const topLocations = freelanceHubData?.topLocations ?? [];
  const topFields = freelanceHubData?.topFields ?? [];
  const remotePercentage = freelanceHubData?.remotePercentage ?? 0;

  const { introParagraph, faqItems } = generateFreelanceMarketHeaderText({
    locale,
    jobCount,
    topLocations,
    topFields,
    remotePercentage,
  });

  const faqSchema = faqItems.length > 0 ? buildFaqPageSchema(faqItems) : null;

  return (
    <>
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}

      <section
        id="freelance-info-header"
        className={`freelance-info-header relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-[#F8F5F0] via-[#F2EADF] to-[#C8DDCE]/50 dark:from-[#170F07] dark:via-[#2A1D0F] dark:to-[#1A2E22] p-6 sm:p-8 lg:p-12 ${className}`}
        aria-labelledby="freelance-header-title"
      >
        <h1
          id="freelance-header-title"
          className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1E1A14] dark:text-[#F2EADF] mb-3"
        >
          {H1_BY_LOCALE[locale]}
        </h1>

        <p className="text-base sm:text-lg leading-relaxed max-w-5xl text-[#3D3529] dark:text-[#C4A882] mb-6">
          {introParagraph}
        </p>

        {(topLocations.length > 0 || topFields.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            {topLocations.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] mb-2">
                  {POPULAR_LOCATIONS_TITLE[locale]}
                </h2>
                <ul className="flex flex-wrap gap-2">
                  {topLocations.map((loc) => (
                    <li key={loc.location}>
                      <a
                        href={buildFreelanceLocationUrl(locale, loc.location)}
                        className="inline-block rounded-full border border-[#DDD5C8] dark:border-[#4A3520] bg-white/70 dark:bg-[#1F1509]/80 px-3 py-1.5 text-sm text-[#1E1A14] dark:text-[#F2EADF] hover:bg-white dark:hover:bg-[#2A1D0F] transition-colors"
                      >
                        {loc.location.split(',')[0].trim()}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {topFields.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-[#3D3529] dark:text-[#C4A882] mb-2">
                  {POPULAR_FIELDS_TITLE[locale]}
                </h2>
                <ul className="flex flex-wrap gap-2">
                  {topFields.map((f) => (
                    <li key={f.field}>
                      <a
                        href={buildFreelanceFieldUrl(locale, f.field)}
                        className="inline-block rounded-full border border-[#DDD5C8] dark:border-[#4A3520] bg-white/70 dark:bg-[#1F1509]/80 px-3 py-1.5 text-sm text-[#1E1A14] dark:text-[#F2EADF] hover:bg-white dark:hover:bg-[#2A1D0F] transition-colors"
                      >
                        {f.field}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {faqItems.length > 0 && (
          <section aria-labelledby="freelance-faq-title">
            <h2
              id="freelance-faq-title"
              className="text-lg font-semibold text-[#1E1A14] dark:text-[#F2EADF] mb-3"
            >
              {FAQ_SECTION_TITLE[locale]}
            </h2>
            <div className="flex flex-col gap-1">
              {faqItems.map((faq, idx) => (
                <details
                  key={idx}
                  className="group rounded-xl border border-[#DDD5C8]/70 dark:border-[#4A3520]/85 bg-white/85 dark:bg-[#1F1509]/95"
                >
                  <summary className="cursor-pointer list-none px-4 py-3 text-sm font-semibold text-[#1E1A14] dark:text-[#F2EADF] marker:content-none sm:px-5 sm:text-base [&::-webkit-details-marker]:hidden">
                    <span className="flex items-center justify-between gap-3">
                      <span>{faq.question}</span>
                      <span className="text-[#7A6E5F] dark:text-[#B8A48C] -rotate-90 transition group-open:rotate-0" aria-hidden>
                        ▼
                      </span>
                    </span>
                  </summary>
                  <div className="border-t border-[#DDD5C8]/70 dark:border-[#4A3520]/85 px-4 py-3 text-sm leading-relaxed text-[#3D3529] dark:text-[#C4A882] sm:px-5 sm:text-base">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        )}
      </section>
    </>
  );
};
